import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@store';
import {
  PermissionDenied,
  PermissionEnum,
  Table,
  useQueryState,
  useFilters,
  useGetList,
  usePrevious,
  formatDate,
  SelectItemProps,
  IconExpand,
  SchemaFilterType,
} from '@ui/src';
import { useLazyGetTransactionTypesRiskQuery } from '../store/services';
import {
  columns,
  columnsLabels,
  schema,
  filtersSchema,
} from './variables/list';
import { TransactionsTypesRiskScore as TransactionsTypesRiskScoreProps } from '../../types';
import { clearTypesRisk } from '../store/slices/types-risk';

import ROUTES from '../../../routes';

import cn from '../transactions-risk.module.css';

const formatTransactionsTypesList = (
  list: TransactionsTypesRiskScoreProps[],
  transactionTypes?: SelectItemProps[],
): TransactionsTypesRiskScoreProps[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const currentPaymentType = result?.paymentType
      ?.split('_')
      .map((type) => `${type[0]}${type.slice(1).toLowerCase()}`)
      .join(' ');

    const currentTransactionType = transactionTypes?.find(
      (type) => type.value === result.transactionTypeId,
    );

    const render: Record<string, React.JSX.Element> = {
      createdAt: <div>{formatDate(result.createdAt)}</div>,
      paymentType: <div>{currentPaymentType || '-'}</div>,
      transactionTypeId: <div>{currentTransactionType?.name || '-'}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const TransactionsTypesRiskScore = () => {
  const {
    transactionsRisk: {
      typesRisk: { typesRisk, pagination, errorsList },
    },
    amlDictionaries: { activePermissions, transactionTypes, paymentTypes },
  } = useAppSelector((state) => state);

  const [formattedFiltersSchema, setFormattedFiltersSchema] =
    useState(filtersSchema);

  const [getTransactionTypesRisk, { isLoading, isFetching }] =
    useLazyGetTransactionTypesRiskQuery();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);
  const prevFilters = usePrevious(filters);

  const dispatch = useAppDispatch();

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<TransactionsTypesRiskScoreProps>({
      getList: getTransactionTypesRisk,
      onFilterChange,
      onSortChange,
      schema,
    });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: formattedFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatTransactionsTypesList(
      typesRisk as TransactionsTypesRiskScoreProps[],
      transactionTypes?.map((item) => ({
        name: item.name,
        value: item.id,
      })),
    );

    return data;
  }, [transactionTypes, typesRisk]);

  useEffect(() => {
    if (paymentTypes) {
      const nextFiltersSchema = filtersSchema?.map((item) => {
        const result = { ...item };

        if (item.name === 'paymentType') {
          // @ts-ignore
          result.fields = paymentTypes?.map((paymentType) => ({
            name: 'paymentType',
            title: paymentType.name,
            value: paymentType.paymentType,
          }));
        }

        return result;
      });

      setFormattedFiltersSchema(nextFiltersSchema);
    }
  }, [paymentTypes]);

  useEffect(() => {
    setValues(
      'transactionTypeId',
      transactionTypes
        ?.filter(
          (item) =>
            item.defaulName === 'INCOMING' ||
            item.defaulName === 'OUTGOING' ||
            item.defaulName === 'INTERNAL',
        )
        .map((item) => ({
          name: item.name,
          value: item?.id,
        })) as SelectItemProps[],
    );
  }, [transactionTypes]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(
        PermissionEnum.SETTINGS_TRANSACTIONS_TYPES_RISK_SCORE_LIST,
      ) &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !typesRisk
    ) {
      getListAction({
        filters: {
          ...filters,
          take: {
            value: ['30'],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [activePermissions, filters, sort, typesRisk]);
  useEffect(
    () => () => {
      dispatch(clearTypesRisk());
    },
    [dispatch],
  );

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_TRANSACTIONS_TYPES_RISK_SCORE_LIST,
    )
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <Table
        columns={columns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        filtersComponent={filtersComponent}
        sort={sort}
        columnsLabels={columnsLabels}
        onFilterChange={(values) => {
          onFilter({
            ...values,
          });
        }}
        handleSubmit={handleSubmit}
        isLoading={isLoading || isFetching}
        take="30"
        setPage={(data) => {
          setPage({
            ...data,
            filters: {
              ...filters,
            },
          });
        }}
        pagination={pagination}
        topContent={
          <div>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_TRANSACTIONS_TYPES_RISK_SCORE_LIST,
            ) && (
              <NavLink
                to={ROUTES.TRANSACTIONS_RISK_TYPES_RISK_CREATE}
                className={cn.createTransactionType}
              >
                <span>
                  <IconExpand width={16} height={16} />
                </span>
                <span>Add Transactions Types Risk Score</span>
              </NavLink>
            )}
          </div>
        }
        error={errorsList || errorList}
        paginationDisabled={isFetching || isLoading}
        isFetching={isFetching}
        clickable
      />
    );
  }

  return (
    <div className={cn.typesScore}>
      {content}
      <Outlet />
    </div>
  );
};

export default TransactionsTypesRiskScore;
